import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// components
import {
    TextField,
    Button,
    IconButton,
    FormLabel,
    Table,
    TableHead,
    TableFooter,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    TextareaAutosize,
} from "@material-ui/core";
import BouncingDots from "../../../loading/BouncingDots";
import NumericalTextField from "../../../material-components/Misc/NumericalTextField";

// reducers
import { getFlowExternalServiceForSelectedFlowItem } from "../../../../reducers/flowExternalServices";
import { getFlowExternalServiceInputsForSelectedFlowItem } from "../../../../reducers/flowExternalServiceInputs";

// types
import { IAppState } from "../../../../types/stores";

// actions
import {
    updateMultipleAttribute,
    invalidateItemAndChildren,
    newFlowExternalServiceInput,
    deleteFlowExternalServiceInput,
} from "../../../../actions/flowActions";
import { showModal } from "../../../../actions/actionCreators";
import { SetAWSFlowControlFilesInfo } from "../../../../actions/awsActions";
import { GetS3FileInfo, ResetS3Data } from "../../../../actions/awsActions";

import { BrowseFileLocations } from "./FlowControlInput";
import { IFileInfo } from "../../../../types/actions/fileInfo";
import { getFlowExternalServiceParametersForSelectedFlowItem } from "../../../../reducers/flowExternalServiceParameters";
import { FlowExternalServiceParameter } from "../../../../types/stores/flowTypes";
import { IExternalServiceParameter } from "../../../../types/stores/flowControlData";
import { getItemErrorsForSelectedFlow } from "../../../../reducers/flowItems";
import { BrowseFileIntegrations } from "../../../../helpers/constants";
import { IS3Input } from "../../../modals/manage-files/browseFilesModal";
import { GetNasFileInfo } from "../../../../actions/nasActions";

interface IProps {
    fileLocationName: string;
}

const FlowControlInputFiles: React.FC<IProps> = ({ fileLocationName }: IProps) => {
    const dispatch = useDispatch();
    const loadingPreviewLabel = "Loading...";

    // State
    const [editFileNumber, setEditFileNumber] = useState(0);
    const [editFileName, setEditFileName] = useState("");
    const [editFileQty, setEditFileQty] = useState("");
    const [totalInputQty, setTotalInputQty] = useState(0);
    const [openFilePreviews, setOpenFilePreviews] = useState<Array<number>>([]);
    const [fetchingInputId, setFetchingInputId] = useState(0);
    const [isTestMode, setIsTestMode] = useState(false);
    const [testLimitValue, setTestLimitValue] = useState(0);
    const [duplicateInputs, setDuplicateInputs] = useState<Array<string>>([]);

    // Redux

    // S3
    const awsFilesInfo = useSelector<IAppState, Array<IFileInfo>>(state => state.aws.flowControlFilesInfo);
    const awsIsDownloading = useSelector<IAppState, boolean>(state => state.aws.isDownloading);
    const awsFileName = useSelector<IAppState, string>(state => state.aws.fileName);
    const awsFilePreview = useSelector<IAppState, string>(state => state.aws.filePreview || "");
    const awsFileQuantity = useSelector<IAppState, number>(state => state.aws.fileQuantity || 0);
    const awsError = useSelector((state: IAppState) => state.aws.error);

    // NAS
    const nasFilesInfo = useSelector((state: IAppState) => state.nas.filesInfo);

    // Parameters
    const flowServiceParameters = useSelector<IAppState, Array<FlowExternalServiceParameter>>(state =>
        getFlowExternalServiceParametersForSelectedFlowItem(state)
    );
    const testSwitchParam = useSelector<IAppState, IExternalServiceParameter | undefined>(state =>
        state.flowControlData.externalServiceParameters.find(x => x.ExternalParameter.ParameterName == "Test_Switch")
    );
    const testLimitValueParam = useSelector<IAppState, IExternalServiceParameter | undefined>(state =>
        state.flowControlData.externalServiceParameters.find(
            x => x.ExternalParameter.ParameterName == "Test_Limit_Value"
        )
    );

    // Others
    const flowItemErrors = useSelector(state => getItemErrorsForSelectedFlow(state));
    const flowExternalService = useSelector((state: IAppState) => getFlowExternalServiceForSelectedFlowItem(state));
    const flowExternalServiceInputs = useSelector((state: IAppState) =>
        getFlowExternalServiceInputsForSelectedFlowItem(state)
    );

    useEffect(() => {
        setDuplicateInputs([]);
        if (flowExternalServiceInputs.length > 0) {
            let total = 0;
            if (isTestMode) {
                total = flowExternalServiceInputs.length * testLimitValue;
            } else {
                total = flowExternalServiceInputs.reduce((x, { InputQuantity }) => x + InputQuantity, 0);
            }
            setTotalInputQty(total);

            if (flowItemErrors[flowExternalService.FlowItemId]) {
                const errors = flowItemErrors[flowExternalService.FlowItemId];
                let dupes: Array<string> = [];
                errors.forEach((x: string) => {
                    if (x.includes("Input File Name is duplicated:")) {
                        const index = x.indexOf(":");
                        const name = x.substring(index + 2, x.length);
                        dupes.push(name);
                    }
                });
                if (dupes.length > 0) {
                    setDuplicateInputs(dupes);
                }
            }
        } else if (fileLocationName == BrowseFileLocations.PLEX5) {
            addNewInput("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowExternalServiceInputs, fileLocationName, isTestMode]);

    const updateFileInfo = (name: string, preview: string = "", quantity: number = 0, add: boolean = false) => {
        let updFileInfo = [...awsFilesInfo];
        if (add) {
            const fileInfo = updFileInfo.find(x => x.fileName == name);
            if (!fileInfo) {
                updFileInfo.push({
                    fileName: name,
                    filePreview: preview,
                    fileQuantity: quantity,
                });
            }
        } else {
            const preview = updFileInfo.find(x => x.fileName == name);
            if (preview) {
                updFileInfo.splice(updFileInfo.indexOf(preview), 1);
            }
        }
        dispatch(SetAWSFlowControlFilesInfo(updFileInfo));
    };

    // S3, getting file info
    useEffect(() => {
        if (!awsIsDownloading) {
            const fetchFile = flowExternalServiceInputs.find(x => x.InputId == fetchingInputId);
            if (fetchFile && fetchFile.InputName == awsFileName) {
                let preview = "";
                if (awsError) {
                    preview = awsError.toString();
                } else if (awsFilePreview) {
                    preview = awsFilePreview;
                } else {
                    preview = "Not Found";
                }

                updateFileInfo(fetchFile.InputName, preview, awsFileQuantity, true);
                setFetchingInputId(0);
                dispatch(ResetS3Data());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, updateFileInfo, awsIsDownloading, awsError, awsFilePreview, awsFileQuantity]);

    // Manage TEST Mode service parameter
    useEffect(() => {
        if (flowServiceParameters && testSwitchParam && testLimitValueParam && flowExternalServiceInputs.length > 0) {
            const testSwitchParamValue = flowServiceParameters?.find(
                x => x.ParameterId == testSwitchParam?.ParameterId
            )?.Value;

            if (testSwitchParamValue == "TEST") {
                setIsTestMode(true);

                const testLimitValueParamValue = parseInt(
                    flowServiceParameters?.find(x => x.ParameterId == testLimitValueParam?.ParameterId)?.Value ?? "0"
                );
                setTestLimitValue(testLimitValueParamValue);
            } else {
                setIsTestMode(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowServiceParameters, testSwitchParam, testLimitValueParam, flowExternalServiceInputs]);

    const addNewInput = (name: string, id: number = -1, quantity: number = 0, nasServerId: number = 0) => {
        const toAdd = id >= 0 ? id : 1;
        const nextNumber =
            flowExternalServiceInputs.length > 0
                ? Math.max(...flowExternalServiceInputs.map(x => x.InputNumber)) + toAdd
                : toAdd;
        dispatch(newFlowExternalServiceInput(flowExternalService.FlowItemId, nextNumber, name, quantity, nasServerId));
        dispatch(invalidateItemAndChildren(flowExternalService.FlowItemId));
    };

    const addNewFile = () => {
        if (fileLocationName == BrowseFileLocations.PLEX5) {
            addNewInput("");
        } else {
            // S3 will pop Modal for selection
            dispatch(
                showModal("BROWSE_FILES", {
                    handleModalFile,
                    requestor: "flow",
                    browseFileAllowed:
                        fileLocationName == BrowseFileLocations.S3
                            ? BrowseFileIntegrations.S3
                            : BrowseFileIntegrations.SERVER,
                })
            );
        }
    };

    const handleModalFile = (source, files: any, nasServerId: number = 0) => {
        if (source == BrowseFileIntegrations.SERVER) {
            (files as Array<IFileInfo>).forEach((file, index) => {
                addNewInput(file.fileName, index + 1, file.fileQuantity, nasServerId);
            });
        } else {
            (files as Array<IS3Input>).forEach(file => {
                addNewInput(file.name, file.id);
            });
        }
    };

    const deleteFile = (inputId: number) => {
        dispatch(deleteFlowExternalServiceInput(inputId));
        setEditFileNumber(0);
        setEditFileName("");
        setEditFileQty("");
        const file = flowExternalServiceInputs.find(x => x.InputId == inputId);
        if (file) {
            updateFileInfo(file.InputName);
        }
    };

    const previewFile = (inputId: number, file: string, nasServerId: number = 0) => {
        let updatePreviews = [...openFilePreviews];
        if (openFilePreviews.includes(inputId)) {
            updatePreviews.splice(updatePreviews.indexOf(inputId), 1);
        } else {
            updatePreviews.push(inputId);
            if (getPreviewData(file) == loadingPreviewLabel) {
                setFetchingInputId(inputId);

                if (fileLocationName == BrowseFileLocations.S3) {
                    dispatch(GetS3FileInfo(file));
                } else {
                    // NAS
                    dispatch(GetNasFileInfo(nasServerId, file));
                }
            }
        }
        setOpenFilePreviews(updatePreviews);
    };

    const isPreviewOpen = (inputId: number): boolean => openFilePreviews.includes(inputId);

    const getPreviewData = (name: string): string => {
        if (fileLocationName == BrowseFileLocations.S3) {
            return awsFilesInfo.find(x => x.fileName == name)?.filePreview ?? loadingPreviewLabel;
        } else {
            // NAS
            return nasFilesInfo.find(x => x.fileName == name)?.filePreview ?? loadingPreviewLabel;
        }
    };

    const getFileQuantity = (inputId: number, name: string): number => {
        const fileInfo = awsFilesInfo.find(x => x.fileName == name);
        if (fileInfo) {
            dispatch(
                updateMultipleAttribute("flowExternalServiceInputs", inputId, {
                    InputQuantity: fileInfo.fileQuantity,
                })
            );

            return fileInfo.fileQuantity;
        } else {
            return 0;
        }
    };

    const updateFileName = (inputId: number, name: string) => {
        dispatch(
            updateMultipleAttribute("flowExternalServiceInputs", inputId, {
                InputName: name,
            })
        );
    };

    const updateFileQty = (inputId: number, qty: string) => {
        dispatch(
            updateMultipleAttribute("flowExternalServiceInputs", inputId, {
                InputQuantity: qty == "" ? 0 : parseInt(qty),
            })
        );
    };

    const isDupeFile = (file: string) => duplicateInputs.includes(file);

    return (
        <>
            <div style={{ marginBottom: "20px" }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "20px",
                        marginBottom: "10px",
                    }}
                >
                    <FormLabel>Input Files</FormLabel>
                </div>
                <div
                    className={
                        fileLocationName == BrowseFileLocations.PLEX5
                            ? "flowcontrol-plex5-table"
                            : "flowcontrol-s3-table"
                    }
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    className={
                                        fileLocationName == BrowseFileLocations.PLEX5
                                            ? "flowcontrol-plex5-file"
                                            : "flowcontrol-s3-file"
                                    }
                                >
                                    File Name
                                </TableCell>
                                <TableCell style={{ width: "10%", fontSize: 15, padding: "5px" }}>
                                    <p>Quantity</p>
                                    {isTestMode ? (
                                        <i>
                                            <b>(TEST Mode)</b>
                                        </i>
                                    ) : null}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        id="add-row"
                                        variant="contained"
                                        color="secondary"
                                        style={{ fontSize: "10px" }}
                                        startIcon={<i className="material-icons">add</i>}
                                        disabled={fileLocationName == ""}
                                        onClick={addNewFile}
                                    >
                                        Add File
                                    </Button>
                                </TableCell>
                                {(fileLocationName == BrowseFileLocations.S3 ||
                                    fileLocationName == BrowseFileLocations.SERVER) && <TableCell />}
                            </TableRow>
                        </TableHead>
                        {fileLocationName != "" && flowExternalServiceInputs.length > 0 && (
                            <TableBody>
                                {flowExternalServiceInputs.map((file, index) => (
                                    <>
                                        {fileLocationName == BrowseFileLocations.PLEX5 ? (
                                            <TableRow key={"plex" + index}>
                                                <TableCell
                                                    style={{ fontSize: 15, padding: "5px" }}
                                                    className={
                                                        isDupeFile(file.InputName)
                                                            ? "alert-warning-original flow-item-warnings"
                                                            : ""
                                                    }
                                                >
                                                    <TextField
                                                        id={"file-name-" + file.InputNumber}
                                                        type="text"
                                                        style={{
                                                            width: "100%",
                                                            fontSize: "15px",
                                                        }}
                                                        value={
                                                            file.InputNumber == editFileNumber
                                                                ? editFileName
                                                                : file.InputName
                                                        }
                                                        onChange={e => {
                                                            setEditFileNumber(file.InputNumber);
                                                            setEditFileName(e.target.value);
                                                            setEditFileQty(
                                                                file.InputQuantity > 0
                                                                    ? file.InputQuantity.toString()
                                                                    : ""
                                                            );
                                                        }}
                                                        onBlur={e => updateFileName(file.InputId, e.target.value)}
                                                        inputProps={{ maxLength: 44 }}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{ width: "10%", fontSize: 15, padding: "5px" }}
                                                >
                                                    <NumericalTextField
                                                        id={"file-qty-" + file.InputNumber}
                                                        value={
                                                            isTestMode
                                                                ? testLimitValue
                                                                : file.InputNumber == editFileNumber
                                                                ? editFileQty
                                                                : file.InputQuantity == 0
                                                                ? ""
                                                                : file.InputQuantity
                                                        }
                                                        onChange={e => {
                                                            setEditFileNumber(file.InputNumber);
                                                            setEditFileName(file.InputName);
                                                            setEditFileQty(e.target.value);
                                                        }}
                                                        onBlur={e => updateFileQty(file.InputId, e.target.value)}
                                                        style={{ width: "100%", fontSize: "15px" }}
                                                        disabled={isTestMode}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Delete File">
                                                        <IconButton
                                                            aria-label="delete"
                                                            color="secondary"
                                                            size="small"
                                                            onClick={() => deleteFile(file.InputId)}
                                                        >
                                                            <i className="material-icons">delete</i>
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <>
                                                <TableRow key={fileLocationName + index}>
                                                    <TableCell
                                                        style={{ fontSize: 15, padding: "5px" }}
                                                        className={
                                                            isDupeFile(file.InputName)
                                                                ? "alert-warning-original flow-item-warnings"
                                                                : ""
                                                        }
                                                    >
                                                        <TextField
                                                            id={"file-name-" + file.InputNumber}
                                                            type="text"
                                                            style={{ width: "100%", fontSize: "15px" }}
                                                            value={file.InputName}
                                                            inputProps={{ readOnly: true }}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        style={{ width: "10%", fontSize: 15, padding: "5px" }}
                                                    >
                                                        <NumericalTextField
                                                            id={"file-qty-" + file.InputNumber}
                                                            value={
                                                                isTestMode
                                                                    ? testLimitValue
                                                                    : file.InputNumber == editFileNumber
                                                                    ? editFileQty
                                                                    : file.InputQuantity == 0
                                                                    ? getFileQuantity(file.InputId, file.InputName)
                                                                    : file.InputQuantity
                                                            }
                                                            onChange={e => {
                                                                setEditFileNumber(file.InputNumber);
                                                                setEditFileName(file.InputName);
                                                                setEditFileQty(e.target.value);
                                                            }}
                                                            onBlur={e => updateFileQty(file.InputId, e.target.value)}
                                                            style={{ width: "100%", fontSize: "15px" }}
                                                            disabled={isTestMode}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center" style={{ width: "10%" }}>
                                                        <Tooltip
                                                            title={
                                                                isPreviewOpen(file.InputId)
                                                                    ? "Close Preview"
                                                                    : "Preview File"
                                                            }
                                                        >
                                                            <IconButton
                                                                id={"view-preview" + index}
                                                                aria-label="delete"
                                                                color={
                                                                    isPreviewOpen(file.InputId) ? "primary" : "default"
                                                                }
                                                                size="small"
                                                                onClick={() =>
                                                                    previewFile(
                                                                        file.InputId,
                                                                        file.InputName,
                                                                        file.NasServerId
                                                                    )
                                                                }
                                                            >
                                                                <i className="material-icons">preview</i>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete File">
                                                            <IconButton
                                                                aria-label="delete"
                                                                color="secondary"
                                                                size="small"
                                                                onClick={() => deleteFile(file.InputId)}
                                                            >
                                                                <i className="material-icons">delete</i>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell style={{ width: "25%" }} />
                                                </TableRow>
                                                {isPreviewOpen(file.InputId) && (
                                                    <TableRow key={"preview" + index}>
                                                        <TableCell colSpan={4}>
                                                            {awsIsDownloading && file.InputId == fetchingInputId ? (
                                                                <div
                                                                    style={{
                                                                        marginTop: "20px",
                                                                        width: "100%",
                                                                        textAlign: "center",
                                                                    }}
                                                                >
                                                                    <BouncingDots />
                                                                </div>
                                                            ) : (
                                                                <TextareaAutosize
                                                                    className="display-preview"
                                                                    style={{
                                                                        width: "100%",
                                                                        minHeight: "25px",
                                                                        maxHeight: "130px",
                                                                        resize: "none",
                                                                        overflow: "auto",
                                                                    }}
                                                                    value={getPreviewData(file.InputName)}
                                                                    disabled
                                                                />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        )}
                                    </>
                                ))}
                            </TableBody>
                        )}
                        {fileLocationName != "" && flowExternalServiceInputs.length > 1 && (
                            <TableFooter>
                                <TableRow>
                                    <TableCell
                                        align="right"
                                        style={{
                                            width: "51%",
                                            fontSize: 15,
                                            fontWeight: "bold",
                                            padding: "5px",
                                        }}
                                    >
                                        Total Quantity
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        style={{
                                            width: "10%",
                                            fontSize: 15,
                                            fontWeight: "bold",
                                            padding: "5px",
                                        }}
                                    >
                                        {totalInputQty.toLocaleString()}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableFooter>
                        )}
                    </Table>
                </div>
            </div>
        </>
    );
};
export default FlowControlInputFiles;
